const AUTOPLAY_DELAY_MS = 5000;
const DRAG_SENSITIVITY = 1.5;
const BREAKPOINTS = [
    { minWidth: 992, slidesPerView: 3 },
    { minWidth: 768, slidesPerView: 2 },
];

/**
 * Scroll-snap carousel with dots, drag-to-scroll and autoplay (paused on hover / touch / drag).
 * Expects an `x-ref="track"` element holding the slides, and works in both LTR and RTL.
 * Bind `@scroll.debounce.50ms="onScroll()"` on the track and `scrollToPage(i)` on the dots.
 */
export default function customCarousel() {
    let autoplayTimer = null;
    let resizeObserver = null;
    let stopListening = null;

    return {
        activePage: 0,
        totalPages: 0,
        slidesPerView: 1,

        init() {
            const track = this.$refs.track;

            this.updateDimensions();

            resizeObserver = new ResizeObserver(() => this.updateDimensions());
            resizeObserver.observe(track);

            stopListening = new AbortController();
            this.listenForPointer(track, stopListening.signal);
            this.startAutoplay();
        },

        destroy() {
            resizeObserver?.disconnect();
            stopListening?.abort();
            this.stopAutoplay();
        },

        get slides() {
            return Array.from(this.$refs.track?.children ?? []);
        },

        get isRtl() {
            return getComputedStyle(this.$refs.track).direction === 'rtl';
        },

        updateDimensions() {
            this.slidesPerView = BREAKPOINTS.find(({ minWidth }) => window.innerWidth >= minWidth)?.slidesPerView ?? 1;
            this.totalPages = Math.max(1, this.slides.length - this.slidesPerView + 1);

            this.updateActivePage();
        },

        onScroll() {
            this.updateActivePage();
        },

        /** The active page is the slide whose leading edge sits closest to the track's leading edge. */
        updateActivePage() {
            if (this.slides.length === 0) {
                return;
            }

            const trackRect = this.$refs.track.getBoundingClientRect();
            const edge = this.isRtl ? 'right' : 'left';

            const distances = this.slides.map((slide) =>
                Math.abs(slide.getBoundingClientRect()[edge] - trackRect[edge])
            );
            const closest = distances.indexOf(Math.min(...distances));

            this.activePage = Math.min(closest, this.totalPages - 1);
        },

        scrollToPage(index) {
            const slide = this.slides[index];

            if (!slide) {
                return;
            }

            const edge = this.isRtl ? 'right' : 'left';
            const offset = slide.getBoundingClientRect()[edge] - this.$refs.track.getBoundingClientRect()[edge];

            this.$refs.track.scrollBy({ left: offset, behavior: 'smooth' });
            this.activePage = index;
        },

        startAutoplay() {
            if (autoplayTimer) {
                return;
            }

            autoplayTimer = setInterval(() => {
                this.scrollToPage(this.activePage + 1 >= this.totalPages ? 0 : this.activePage + 1);
            }, AUTOPLAY_DELAY_MS);
        },

        stopAutoplay() {
            clearInterval(autoplayTimer);
            autoplayTimer = null;
        },

        /**
         * Mouse drag-to-scroll, plus hover / touch handling that pauses autoplay.
         *
         * @param {HTMLElement} track
         * @param {AbortSignal} signal Removes every listener when aborted.
         */
        listenForPointer(track, signal) {
            let isDragging = false;
            let startX = 0;
            let startScroll = 0;

            const endDrag = () => {
                if (!isDragging) {
                    return;
                }

                isDragging = false;
                track.style.scrollSnapType = '';
                track.style.scrollBehavior = '';
                track.style.cursor = '';

                this.startAutoplay();
                this.updateActivePage();
            };

            track.addEventListener(
                'mousedown',
                (event) => {
                    isDragging = true;
                    startX = event.pageX - track.offsetLeft;
                    startScroll = track.scrollLeft;

                    this.stopAutoplay();
                    track.style.scrollSnapType = 'none';
                    track.style.scrollBehavior = 'auto';
                    track.style.cursor = 'grabbing';
                },
                { signal }
            );

            track.addEventListener(
                'mousemove',
                (event) => {
                    if (!isDragging) {
                        return;
                    }

                    event.preventDefault();
                    track.scrollLeft = startScroll - (event.pageX - track.offsetLeft - startX) * DRAG_SENSITIVITY;
                },
                { signal }
            );

            track.addEventListener('mouseup', endDrag, { signal });
            track.addEventListener('mouseenter', () => this.stopAutoplay(), { signal });
            track.addEventListener(
                'mouseleave',
                () => {
                    endDrag();
                    this.startAutoplay();
                },
                { signal }
            );
            track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true, signal });
            track.addEventListener('touchend', () => this.startAutoplay(), { passive: true, signal });
            track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
        },
    };
}
